import { HttpEventType } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { createHttpContext } from '@app/core/http/http-context';
import { Group } from '@app/shared/models/classes/Group';
import {
  ApplicationUser,
  ResetPasswordBody,
  UserCreateDto,
  UserUpdateDto,
} from '@app/shared/models/classes/ApplicationUser';
import { environment } from '@env/environment';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { FileUploadProgress, FileUploadService } from '../components/files/file-upload.service';
import { ApiUrlService } from './api-url.service';
import { EntityService } from './entity.service';
import { Response } from './local/Response';

export interface UserGroup extends Pick<Group, 'id' | 'displayName'> {
  id: string;
  displayName: string;
}

@Service({ autoProvided: false })
export class UsersService extends EntityService<ApplicationUser, UserCreateDto, UserUpdateDto> {
  private readonly apiUrlService = inject(ApiUrlService);

  constructor() {
    super('users');
  }

  getCurrentUser(): Observable<ApplicationUser> {
    return this.httpClient
      .get<Response<ApplicationUser>>(`${this.apiUrlService.getApiUrl()}users/me`)
      .pipe(map((result: Response<ApplicationUser>) => result.data));
  }

  resetPassword(userId: string, resetPasswordDto: ResetPasswordBody) {
    return this.httpClient
      .patch<Response<boolean>>(
        `${environment.authorizationServerUrl}api/v1/users/${userId}/password`,
        resetPasswordDto,
        {
          context: createHttpContext({ skipApiPrefix: true }),
        },
      )
      .pipe(map((result: Response<boolean>) => result.code.statusCode === 200));
  }

  sendPasswordResetEmail(email: string) {
    return this.httpClient
      .post<Response<boolean>>(`users/password-reset`, email)
      .pipe(map((result: Response<boolean>) => result.code.statusCode === 200));
  }

  resendUserInviteEmail(userId: string) {
    return this.httpClient
      .post<Response<boolean>>(`users/${userId}/resend-invite`, userId)
      .pipe(map((result: Response<boolean>) => result.code.statusCode === 200));
  }

  getMentionableUsers(searchString: string): Observable<ApplicationUser[]> {
    return this.httpClient
      .get<Response<ApplicationUser[]>>(`users?filter=${searchString}&isMentionable=true`)
      .pipe(map((result: Response<ApplicationUser[]>) => result.data));
  }

  /**
   * Whether the signed-in account has a local password. Accounts created through SSO have
   * none and cannot pass the current-password check of the password change. If the status
   * cannot be read, this answers true so password accounts keep their password change.
   */
  hasLocalPassword(): Observable<boolean> {
    return this.httpClient
      .get<Response<{ hasPassword: boolean }>>(`${environment.authorizationServerUrl}api/v1/users/me/password`, {
        // A failed status check must neither toast nor tear the session down through a token refresh.
        context: createHttpContext({ skipApiPrefix: true, skipErrorHandler: true, skipTokenRefresh: true }),
      })
      .pipe(
        map((result: Response<{ hasPassword: boolean }>) => result.data?.hasPassword !== false),
        catchError(() => of(true)),
      );
  }

  changePassword(currentPassword: string, newPassword: string): Observable<boolean> {
    return this.httpClient
      .patch<
        Response<boolean>
      >(`${environment.authorizationServerUrl}api/v1/users/me/password`, { currentPassword, password: newPassword }, { context: createHttpContext({ skipApiPrefix: true }) })
      .pipe(map((result: Response<boolean>) => result.code.statusCode === 200));
  }

  deleteManyByIds(ids: string[]): Observable<boolean> {
    const filter = ids.map((id) => `ids=${id}&`).join('');
    return this.httpClient
      .delete<Response<boolean>>(`users?${filter}`)
      .pipe(map((response: Response<boolean>) => response.code.statusCode === 200));
  }

  changeProfileImageByUserId(userId: string, image: Blob): Observable<FileUploadProgress> {
    const formData = new FormData();
    formData.append('file', image);

    return this.httpClient
      .post(`users/${userId}/images`, formData, { reportProgress: true, observe: 'events' })
      .pipe(
        map((event: { type: HttpEventType; loaded?: number; total?: number }) =>
          FileUploadService.mapHttpEventToProgress(event),
        ),
      );
  }

  createUsersByFile(file: File): Observable<FileUploadProgress> {
    const formData = new FormData();
    formData.append('file', file);

    return this.httpClient
      .post(`users`, formData, {
        context: createHttpContext({ skipErrorHandler: true }),
        reportProgress: true,
        observe: 'events',
      })
      .pipe(
        map((event: { type: HttpEventType; loaded?: number; total?: number }) =>
          FileUploadService.mapHttpEventToProgress(event),
        ),
      );
  }

  findByEmail(email: string): Observable<ApplicationUser[]> {
    return this.httpClient
      .get<Response<ApplicationUser[]>>(`users?filter=${encodeURIComponent(email)}&take=1`)
      .pipe(map((result: Response<ApplicationUser[]>) => result.data));
  }

  getUserGroups(userId: string): Observable<UserGroup[]> {
    return this.httpClient
      .get<Response<Omit<Group, 'users'>[]>>(`users/${userId}/groups`)
      .pipe(map(({ data: groups }) => groups.map(({ id, displayName }) => ({ id, displayName }))));
  }
}

export class GetUserPermission {
  permissions: string[] = [];
  useCaseIds: string[] = [];
}
